import React from "react";

import { bindActionCreators } from "@reduxjs/toolkit";
import { useForm } from "react-hook-form";
import { connect } from "react-redux";

import Button from "@busbud/design-system-components/dist/Button";
import Dialog from "@busbud/design-system-components/dist/Dialog";
import DialogActions from "@busbud/design-system-components/dist/DialogActions";
import DialogContent from "@busbud/design-system-components/dist/DialogContent";
import DialogTitle from "@busbud/design-system-components/dist/DialogTitle";
import Label from "@busbud/design-system-components/dist/Label";
import Stack from "@busbud/design-system-components/dist/Stack";
import Text from "@busbud/design-system-components/dist/Text";
import { Input } from "@busbud/horizon";

import { useAppContext } from "@app/helpers/hooks";
import type { NapiUser } from "@app/types/napi-types";

import type { Dispatch } from "@reduxjs/toolkit";
import type { SubmitHandler } from "react-hook-form";

import { useStyles } from "./prompt-password.styles";
import { getErrorText } from "../../../modules/account/utils";
import { closeModal } from "../../../store/state/modals";

const PASSWORD_FIELD_NAME = "password";
interface Props {
  updated_email: string;
  onEmailUpdated: (response: NapiUser) => void;
  onClosed: () => void;
}

interface ReduxProps {
  closeModal: () => void;
  user: NapiUser;
}

const PromptPasswordComponent: React.FC<ReduxProps & Props> = ({
  updated_email,
  user,
  onEmailUpdated,
  closeModal,
  onClosed
}) => {
  const { api, translator } = useAppContext();
  const { classes } = useStyles();
  const methods = useForm({
    defaultValues: { password: "" }
  });
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isDirty, isValid, isSubmitting }
  } = methods;

  const submitForm: SubmitHandler<{ password: string }> = async values => {
    try {
      const response = await api.updateUserEmail(
        user.id,
        updated_email,
        values.password
      );

      if (response.error) {
        // when incorrect, api returns "InputInvalid" which points to a poor error message
        setPasswordInvalidField();
      } else {
        onEmailUpdated(response);
        closeModal();
      }
    } catch (err) {
      // when incorrect, api returns "InputInvalid" which points to a poor error message
      if (err?.response?.data?.error?.type === "InputInvalid") {
        setPasswordInvalidField();
      } else {
        setError(PASSWORD_FIELD_NAME, {
          message: getErrorText(
            translator,
            `password.${err.response.data.error.type}`
          )
        });
      }
    }
  };

  const setPasswordInvalidField = () => {
    setError(PASSWORD_FIELD_NAME, {
      message: getErrorText(translator, "PasswordInvalid")
    });
  };

  return (
    <Dialog open={true} maxWidth="xs" fullWidth={true}>
      <form onSubmit={handleSubmit(submitForm)}>
        <DialogTitle
          classes={{
            root: classes.root
          }}
        >
          <Label size="lg" fontWeight="bold">
            {translator.t("!accounts.menu.update-email.title")}
          </Label>
        </DialogTitle>
        <DialogContent classes={{ root: classes.content }}>
          <Stack direction="column" spacing={2}>
            <Text size="lg">
              {translator.t("!accounts.menu.update-email.password")}
            </Text>
            <Input
              {...register(PASSWORD_FIELD_NAME, { required: true })}
              type="password"
              defaultValue=""
              data-testid="password-field"
              labelText={translator.t("!account.signin.password")}
              isInvalid={!!errors.password}
              helperText={errors.password?.message}
              className="w-full"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- TODO: [fix-jsx-a11y]
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions direction="horizontal" className={classes.actions}>
          <Stack spacing={1} justifyContent="flex-end">
            <Button
              size="md"
              onClick={() => {
                closeModal();
                onClosed();
              }}
              data-testid="cancel-button"
              color="tertiary"
              disabled={isSubmitting}
            >
              {translator.t("!accounts.passengers.delete-passenger.cancel")}
            </Button>
            <Button
              size="md"
              id="submit"
              type="submit"
              data-testid="submit-button"
              disabled={!isDirty || isSubmitting || !isValid}
            >
              {translator.t("!account.reset-password.submit")}
            </Button>
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators({ closeModal }, dispatch);

const mapStateToProps = (state: { user: NapiUser }) => {
  return { user: state.user };
};

export const PromptPassword = connect(
  mapStateToProps,
  mapDispatchToProps
)(PromptPasswordComponent);
